// Lightweight observability hook (dev console + optional redacted remote reporting).
// Never logs passwords, API keys, JWTs, emails, or financial/PII values.

const IS_DEV = process.env.NODE_ENV !== "production";
const REPORT_URL = process.env.REACT_APP_ERROR_REPORTING_URL; // optional; unset = no remote reporting

// Object keys whose values are always fully redacted.
const SENSITIVE_KEY = /(pass|password|token|secret|api[-_]?key|authorization|auth|jwt|ssn|ein|duns|card|cvv|cvc|iban|account|routing|balance|amount|phone|dob)/i;

// Value patterns scrubbed from any string.
const PATTERNS = [
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[REDACTED_JWT]"],
  [/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [REDACTED]"],
  [/sk-[A-Za-z0-9-]{8,}/g, "[REDACTED_KEY]"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi, "[REDACTED_EMAIL]"],
  [/\b\d{9,19}\b/g, "[REDACTED_NUM]"],
];

function scrubString(s) {
  let out = s;
  for (const [re, rep] of PATTERNS) out = out.replace(re, rep);
  return out;
}

function scrubObject(value, depth) {
  const out = {};
  for (const k of Object.keys(value)) {
    out[k] = SENSITIVE_KEY.test(k) ? "[REDACTED]" : scrub(value[k], depth + 1);
  }
  return out;
}

function scrub(value, depth = 0) {
  if (value == null) return value;
  const t = typeof value;
  if (t === "string") return scrubString(value);
  if (t === "number" || t === "boolean") return value;
  if (depth >= 5) return "[…]";
  if (value instanceof Error) return { name: value.name, message: scrubString(value.message || "") };
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => scrub(v, depth + 1));
  if (t === "object") return scrubObject(value, depth);
  return "[unserializable]";
}

function buildEntry(level, moduleName, message, detail) {
  return {
    timestamp: new Date().toISOString(),
    level,
    module: moduleName || "app",
    message: scrubString(String(message ?? "")),
    detail: detail === undefined ? undefined : scrub(detail),
  };
}

function report(entry) {
  if (!REPORT_URL) return;
  try {
    const body = JSON.stringify(entry);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_URL, new Blob([body], { type: "application/json" }));
    } else {
      fetch(REPORT_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch (err) {
    // Logging must never throw; surface the failure only during development.
    // eslint-disable-next-line no-console
    if (IS_DEV) console.error("[logger] remote report failed", err);
  }
}

const CONSOLE_SINK = { error: "error", warn: "warn", info: "info" };

function emit(level, moduleName, message, detail) {
  const entry = buildEntry(level, moduleName, message, detail);
  if (IS_DEV) {
    const sink = console[CONSOLE_SINK[level] || "info"];
    // eslint-disable-next-line no-console
    sink(`[${entry.timestamp}] [${entry.module}] ${entry.message}`, entry.detail ?? "");
  } else if (level === "error" || level === "warn") {
    report(entry);
  }
}

export const logError = (moduleName, message, detail) => emit("error", moduleName, message, detail);
export const logWarn = (moduleName, message, detail) => emit("warn", moduleName, message, detail);
export const logInfo = (moduleName, message, detail) => emit("info", moduleName, message, detail);

let _installed = false;
export function initObservability() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  window.addEventListener("error", (e) => logError("window", "Uncaught error", { message: e.message }));
  window.addEventListener("unhandledrejection", (e) =>
    logError("window", "Unhandled promise rejection", { reason: e?.reason?.message || String(e?.reason) }));
}

initObservability();
